import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuthStore } from '../../store';
import useAdminBranding from '../../hooks/useAdminBranding';
import { menuFor, visibleMenu } from './menuConfig';
import './Sidebar.css';

const Sidebar = ({ isOpen, toggleSidebar, collapsed = false, toggleCollapsed }) => {
    const location = useLocation();
    const { admin } = useAuthStore();
    // On a tenant's own admin address: its name and logo, not HotWiFi's.
    const brand = useAdminBranding();
    // What the superadmin set for this administrator's role and for them (Access Control),
    // loaded by MainLayout. Until it arrives, nothing is hidden.
    const access = useAuthStore((state) => state.access);

    const role = admin?.role;

    // The superadmin always sees everything.
    const menuItems = role === 'superadmin' ? menuFor(role) : visibleMenu(role, access);

    const isActive = (path) => {
        if (path === '/dashboard') return location.pathname === '/dashboard';
        // The visual builder lives under Splash Pages (one menu entry for
        // "what guests see at login") — keep it highlighted while in it.
        if (path === '/templates' && location.pathname.startsWith('/landing-pages')) return true;
        return location.pathname.startsWith(path);
    };

    return (
        <>
            {/* Backdrop for mobile */}
            <div
                className={`sidebar-backdrop${isOpen ? ' visible' : ''}`}
                onClick={toggleSidebar}
                aria-hidden="true"
            />

            <aside className={`sidebar ${isOpen ? 'open' : ''}`} aria-label="Main navigation">
                <div className="sidebar-header">
                    <Link to="/dashboard" className="sidebar-brand" aria-label={`${brand.name} — dashboard`} title={brand.own ? brand.name : undefined}>
                        {brand.own && brand.logoUrl
                            ? <img src={brand.logoUrl} alt="" className="sidebar-brand-logo" />
                            : <span className="sidebar-brand-icon"><i className="fas fa-wifi"></i></span>}
                        <span className="sidebar-brand-text">{brand.name}</span>
                    </Link>
                    <button type="button" className="close-btn" onClick={toggleSidebar} aria-label="Close menu">
                        <i className="fas fa-times"></i>
                    </button>
                </div>

                <div className="sidebar-content">
                    <nav className="sidebar-nav">
                        <ul>
                            {menuItems.map((item, index) => (
                                item.type === 'divider' ? (
                                    <li key={`divider-${index}`} className="menu-divider">
                                        <span>{item.label}</span>
                                    </li>
                                ) : (
                                    <li key={item.path}>
                                        <Link
                                            to={item.path}
                                            className={`menu-link ${isActive(item.path) ? 'active' : ''}`}
                                            aria-current={isActive(item.path) ? 'page' : undefined}
                                            title={collapsed ? item.label : undefined}
                                            onClick={() => window.innerWidth < 768 && toggleSidebar()}
                                        >
                                            <div className="icon-box">
                                                <i className={`fas ${item.icon}`}></i>
                                            </div>
                                            <span>{item.label}</span>
                                            {isActive(item.path) && <div className="active-indicator"></div>}
                                        </Link>
                                    </li>
                                )
                            ))}
                        </ul>
                    </nav>

                    <div className="sidebar-footer">
                        {!collapsed && !brand.own && (
                            <span className="sidebar-credit" style={{ fontSize: '0.7rem', opacity: 0.6, whiteSpace: 'nowrap' }} title="HotWiFi — developed by AsyncPulse Technologies">
                                by AsyncPulse Technologies
                            </span>
                        )}
                        <button
                            type="button"
                            className="sidebar-toggle"
                            onClick={toggleCollapsed}
                            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                        >
                            <i className={`fas fa-angle-${collapsed ? 'right' : 'left'}`}></i>
                        </button>
                    </div>
                </div>
            </aside>
        </>
    );
};

export default Sidebar;

